import React, { useState, useEffect, useRef } from 'react';
import speechRecognitionService from '../../services/speechRecognition';
import { useTranslation } from '../../i18n/translations';
import { useLanguage } from '../../context/LanguageContext';

const MAX_WORDS = 100;
const MAX_RECORDING_TIME = 30; // 30 secondi per 100 parole

const FreeTextInput = ({ 
  value, 
  onChange, 
  onSubmit,
  onFocus = null,
  onBlur = null,
  placeholder = "",
  className = "",
  style = {},
  disabled = false
}) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [isRecording, setIsRecording] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [showMicrophone, setShowMicrophone] = useState(true);
  const [isVoiceText, setIsVoiceText] = useState(false); // Flag per indicare se il testo viene dalla voce
  const [timeRemaining, setTimeRemaining] = useState(MAX_RECORDING_TIME);
  const [pendingTranscript, setPendingTranscript] = useState(''); // Testo in attesa da mostrare alla fine
  const stopRecordingIntentionally = useRef(false); // Flag per distinguere stop volontario da timeout
  const accumulatedTextRef = useRef(''); // Riferimento per accumulare il testo tra i riavvii
  const isRecordingRef = useRef(false); // Ref per controllare lo stato di registrazione nel timer
  const finalTranscriptRef = useRef(''); // Ref per salvare il testo finale
  const interimTranscriptRef = useRef(''); // Ref per salvare l'ultimo interim (per catturare l'ultima frase)
  const stopTimeoutRef = useRef(null); // Ref per il timeout di attesa prima di fermare
  
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Conta parole nel valore corrente
  useEffect(() => {
    if (value && typeof value === 'string') {
      const words = value.trim().split(/\s+/).filter(w => w.length > 0);
      setWordCount(words.length);
      
      // Mostra/nascondi microfono in base al contenuto
      setShowMicrophone(value.trim().length === 0);
    } else {
      setWordCount(0);
      setShowMicrophone(true);
    }
  }, [value]);

  // Auto-resize textarea quando il valore cambia
  useEffect(() => {
    if (inputRef.current) {
      const textarea = inputRef.current;
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 80; // maxHeight in pixel
      
      if (scrollHeight > maxHeight) {
        textarea.style.height = maxHeight + 'px';
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = scrollHeight + 'px';
        textarea.style.overflowY = 'hidden';
      }
    }
  }, [value]);

  // Pulisci timer quando il componente viene smontato
  useEffect(() => {
    return () => {
      console.log('🧹 [FreeTextInput] Cleanup componente');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      speechRecognitionService.stop();
    };
  }, []); // Esegui cleanup SOLO quando il componente viene smontato, non ad ogni cambio di isRecording

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    // Se l'utente digita manualmente, PULISCI tutto e non è più testo vocale
    if (!isRecording) {
      setIsVoiceText(false);
      // Pulisci tutto quando l'utente scrive manualmente
      setFinalTranscript('');
      setInterimTranscript('');
      setPendingTranscript('');
      accumulatedTextRef.current = '';
      finalTranscriptRef.current = '';
    }
    
    // Limita a MAX_WORDS parole
    const words = newValue.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length > MAX_WORDS) {
      // Prendi solo le prime MAX_WORDS parole
      const limitedValue = words.slice(0, MAX_WORDS).join(' ');
      onChange(limitedValue);
    } else {
      onChange(newValue);
    }
  };

  // Funzione helper per processare e mostrare il testo finale (dopo che tutto è stato processato)
  const processAndShowFinalText = () => {
    console.log('📝 [FreeTextInput] processAndShowFinalText - elaboro testo finale');
    
    // Aggiorna sempre lo stato UI
    setIsRecording(false);
    setTimeRemaining(MAX_RECORDING_TIME);
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // MOBILE: Il testo è già nell'input (aggiunto blocco per blocco)
      // Aggiungi solo l'ultimo interim se c'è e non è già presente
      const currentValue = value || '';
      const currentInterim = interimTranscriptRef.current.trim();
      const currentFinal = finalTranscriptRef.current.trim();
      
      if (currentInterim && !currentFinal.includes(currentInterim) && !currentValue.includes(currentInterim)) {
        console.log('📱 [FreeTextInput] Mobile - Aggiungo ultimo interim non finalizzato:', currentInterim);
        const newValue = currentValue.trim() 
          ? (currentValue.trim() + ' ' + currentInterim).trim()
          : currentInterim;
        onChange(newValue);
        setIsVoiceText(true);
      }
      
      // Su mobile, il testo è già nell'input, non serve processarlo
      console.log('📱 [FreeTextInput] Mobile - Testo già nell\'input, non processo');
      return; // Esci, non processare
    }
    
    // DESKTOP: Prendi il testo accumulato (già processato da onResult)
    let finalText = accumulatedTextRef.current || (value && typeof value === 'string' ? value.trim() : '');
    
    // Aggiungi l'ultimo final se non è già presente nell'accumulato
    const currentFinal = finalTranscriptRef.current.trim();
    if (currentFinal && finalText && !finalText.includes(currentFinal)) {
      finalText = (finalText + ' ' + currentFinal).trim();
    } else if (currentFinal && !finalText) {
      finalText = currentFinal;
    }
    
    // IMPORTANTE: Se c'è ancora interim che non è diventato final, aggiungilo comunque
    // (potrebbe essere l'ultima frase che l'utente stava dicendo)
    const currentInterim = interimTranscriptRef.current.trim();
    if (currentInterim && !currentFinal.includes(currentInterim) && !finalText.includes(currentInterim)) {
      console.log('📝 [FreeTextInput] Aggiungo anche l\'ultimo interim non finalizzato:', currentInterim);
      finalText = finalText ? (finalText + ' ' + currentInterim).trim() : currentInterim;
    }
    
    // PULISCI il testo: rimuovi duplicazioni e spazi multipli
    if (finalText) {
      // Rimuovi spazi multipli
      finalText = finalText.replace(/\s+/g, ' ').trim();
      
      // Rimuovi parole/frasi duplicate
      // Strategia: dividi in parole e rimuovi sequenze duplicate
      const words = finalText.split(/\s+/);
      const cleanedWords = [];
      let lastWord = '';
      let duplicateCount = 0;
      
      for (let i = 0; i < words.length; i++) {
        const word = words[i].toLowerCase().replace(/[.,!?;:]/g, '');
        const currentWord = words[i];
        
        // Se la parola è identica alla precedente (ignorando punteggiatura), conta come duplicato
        if (word === lastWord && word.length > 0) {
          duplicateCount++;
          // Se abbiamo più di 2 ripetizioni consecutive, salta questa
          if (duplicateCount > 2) {
            continue;
          }
        } else {
          duplicateCount = 0;
        }
        
        cleanedWords.push(currentWord);
        lastWord = word;
      }
      
      finalText = cleanedWords.join(' ');
      
      // Rimuovi anche frasi duplicate (se una frase appare più volte, rimuovila)
      // IMPORTANTE: Su mobile, le frasi possono essere duplicate anche senza punteggiatura
      // Dividi anche per virgole e spazi multipli
      const sentenceDelimiters = /[.!?]\s+|[.!?]$/;
      let sentences = finalText.split(sentenceDelimiters).filter(s => s.trim());
      
      // Se non ci sono frasi con punteggiatura, prova a dividere per pattern comuni
      if (sentences.length <= 1) {
        // Prova a dividere per sequenze di parole duplicate (pattern comune su mobile)
        const words = finalText.split(/\s+/);
        const potentialSentences = [];
        let currentSentence = [];
        
        for (let i = 0; i < words.length; i++) {
          // Se troviamo una ripetizione di 3+ parole, potrebbe essere una nuova frase
          if (i >= 3) {
            const last3 = words.slice(i - 3, i).join(' ').toLowerCase();
            const next3 = words.slice(i, Math.min(i + 3, words.length)).join(' ').toLowerCase();
            if (last3 === next3) {
              // Trovata ripetizione, salva la frase corrente
              if (currentSentence.length > 0) {
                potentialSentences.push(currentSentence.join(' '));
                currentSentence = [];
              }
              i += 2; // Salta le parole duplicate
              continue;
            }
          }
          currentSentence.push(words[i]);
        }
        if (currentSentence.length > 0) {
          potentialSentences.push(currentSentence.join(' '));
        }
        
        if (potentialSentences.length > 1) {
          sentences = potentialSentences;
        }
      }
      
      if (sentences.length > 1) {
        const uniqueSentences = [];
        const seen = new Set();
        
        for (const sentence of sentences) {
          // Normalizza: lowercase, rimuovi punteggiatura, rimuovi spazi multipli
          const normalized = sentence.trim().toLowerCase().replace(/[.,!?;:]/g, '').replace(/\s+/g, ' ');
          if (!seen.has(normalized) && normalized.length > 3) { // Ignora frasi troppo corte
            seen.add(normalized);
            uniqueSentences.push(sentence.trim());
          }
        }
        
        // Se abbiamo rimosso duplicazioni, usa le frasi uniche
        if (uniqueSentences.length < sentences.length) {
          finalText = uniqueSentences.join('. ').trim();
          if (!finalText.endsWith('.') && !finalText.endsWith('!') && !finalText.endsWith('?')) {
            finalText += '.';
          }
          console.log('🧹 [FreeTextInput] Rimosse duplicazioni frasi:', sentences.length, '->', uniqueSentences.length);
        }
      }
      
      // Rimuovi spazi multipli finali
      finalText = finalText.replace(/\s+/g, ' ').trim();
    }
    
    console.log('📝 [FreeTextInput] Testo finale pulito da mostrare:', finalText);
    
    // Mostra il testo accumulato solo alla fine
    if (finalText.trim()) {
      const words = finalText.trim().split(/\s+/).filter(w => w.length > 0);
      if (words.length <= MAX_WORDS) {
        onChange(finalText);
        console.log('✅ [FreeTextInput] Testo mostrato:', finalText);
      } else {
        const limitedText = words.slice(0, MAX_WORDS).join(' ');
        onChange(limitedText);
        console.log('✅ [FreeTextInput] Testo limitato mostrato:', limitedText);
      }
      setIsVoiceText(true);
    } else {
      console.log('⚠️ [FreeTextInput] Nessun testo da mostrare');
    }
    
    // Pulisci sempre gli stati
    setPendingTranscript('');
    accumulatedTextRef.current = '';
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
  };

  // Funzione per fermare SUBITO l'UI (mic bianco, timer fermo)
  const stopUI = () => {
    console.log('🛑 [FreeTextInput] stopUI - fermo UI SUBITO');
    
    // Ferma SUBITO l'UI - l'utente vede che ha premuto stop
    setIsRecording(false);
    setTimeRemaining(MAX_RECORDING_TIME);
    
    // Ferma il timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Marca come fermato
    isRecordingRef.current = false;
    stopRecordingIntentionally.current = true;
  };

  // Funzione per processare il testo quando Speech Recognition finisce (chiamata da onend)
  const processTextWhenFinished = () => {
    console.log('📝 [FreeTextInput] processTextWhenFinished - Speech Recognition finito');
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // MOBILE: Il testo è già nell'input (aggiunto blocco per blocco)
      // Su mobile, aspetta solo 200ms per dare tempo all'ultimo interim di diventare final
      // Poi processa (che su mobile non fa nulla, solo aggiunge interim se necessario)
      console.log('📱 [FreeTextInput] Mobile - Aspetto 200ms per ultimo interim');
      setTimeout(() => {
        console.log('📱 [FreeTextInput] Mobile - Delay scaduto, processo');
        processAndShowFinalText();
      }, 200);
    } else {
      // DESKTOP: Processa subito
      console.log('💻 [FreeTextInput] Desktop - Processo testo subito');
      processAndShowFinalText();
    }
  };

  // Funzione per fermare Speech Recognition (ma UI già fermata)
  const stopSpeechRecognition = () => {
    console.log('🛑 [FreeTextInput] stopSpeechRecognition - fermo Speech Recognition');
    
    // Cancella timeout se esiste
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    
    // IMPORTANTE: Prima di fermare, assicurati di accumulare l'ultimo final
    // perché Speech Recognition potrebbe essere in un riavvio
    const currentFinal = finalTranscriptRef.current.trim();
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (currentFinal && !isMobile) {
      const previousText = accumulatedTextRef.current || '';
      if (!previousText || !previousText.includes(currentFinal)) {
        accumulatedTextRef.current = previousText 
          ? (previousText + ' ' + currentFinal).trim()
          : currentFinal;
        console.log('📝 [FreeTextInput] Desktop - Testo accumulato (prima di stop):', accumulatedTextRef.current);
      }
    }
    
    // Ferma Speech Recognition (onend verrà chiamato quando finisce)
    speechRecognitionService.stop();
  };

  const handleStartRecording = () => {
    console.log('🎤 [FreeTextInput] handleStartRecording chiamato');
    
    if (!speechRecognitionService.getSupported()) {
      console.error('❌ [FreeTextInput] Speech Recognition non supportato');
      alert(t('freeTextSearch.voiceNotSupported'));
      return;
    }

    if (isRecording) {
      console.log('⚠️ [FreeTextInput] Già in registrazione');
      return;
    }

    console.log('✅ [FreeTextInput] Avvio registrazione...');

    // PULISCI tutto quando si ripreme il microfono (nuova registrazione)
    setFinalTranscript('');
    setInterimTranscript('');
    setPendingTranscript('');
    accumulatedTextRef.current = ''; // Reset testo accumulato
    finalTranscriptRef.current = ''; // Reset ref
    setIsVoiceText(false); // Reset flag voce
    setIsRecording(true);
    isRecordingRef.current = true; // Aggiorna ref
    setTimeRemaining(MAX_RECORDING_TIME);
    stopRecordingIntentionally.current = false; // Reset flag

    console.log('⏱️ [FreeTextInput] Avvio timer, MAX_RECORDING_TIME:', MAX_RECORDING_TIME);

    // Avvia timer per limite di tempo - INDIPENDENTE dalla registrazione
    const startTime = Date.now();
    const timerInterval = setInterval(() => {
      console.log('⏱️ [FreeTextInput] Tick timer - isRecordingRef:', isRecordingRef.current, 'stopRecordingIntentionally:', stopRecordingIntentionally.current);
      
      // Controlla se siamo ancora in registrazione usando il ref
      if (!isRecordingRef.current || stopRecordingIntentionally.current) {
        console.log('⏱️ [FreeTextInput] Timer fermato - isRecordingRef:', isRecordingRef.current, 'stopRecordingIntentionally:', stopRecordingIntentionally.current);
        clearInterval(timerInterval);
        timerRef.current = null;
        return;
      }
      
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MAX_RECORDING_TIME * 1000 - elapsed);
      const remainingSeconds = Math.ceil(remaining / 1000);
      
      // Aggiorna SEMPRE il timer visibile ogni tick per forzare il re-render
      setTimeRemaining(remainingSeconds);

      if (remaining <= 0) {
        console.log('⏱️ [FreeTextInput] Timer scaduto! Fermo tutto...');
        // STEP 1: Ferma SUBITO l'UI (mic bianco, timer fermo)
        stopUI();
        
        // STEP 2: Ferma Speech Recognition (onend verrà chiamato quando finisce)
        stopSpeechRecognition();
        
        // Il testo verrà processato quando onend viene chiamato (processTextWhenFinished)
      }
    }, 100);

    timerRef.current = timerInterval;
    console.log('⏱️ [FreeTextInput] Timer salvato nel ref, timerInterval ID:', timerInterval);

    console.log('🎤 [FreeTextInput] Avvio Speech Recognition...');

    // Rileva se siamo su mobile (per gestire diversamente l'accumulo)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Avvia riconoscimento vocale con limite di tempo
    const started = speechRecognitionService.start(
      // onResult
      (final, interim) => {
        console.log('🎤 [FreeTextInput] onResult:', { final, interim, isMobile });
        // IMPORTANTE: Comportamento diverso tra desktop e mobile
        // - DESKTOP: final contiene TUTTO il testo accumulato finora (inizia da zero ogni volta)
        // - MOBILE: final contiene solo l'ULTIMA frase/parola finalizzata (ogni final è nuovo)
        setFinalTranscript(final);
        finalTranscriptRef.current = final; // Salva anche nel ref
        setInterimTranscript(interim);
        interimTranscriptRef.current = interim; // Salva anche l'interim per catturare l'ultima frase
        
        // Gestisci final in modo diverso su mobile vs desktop
        if (final && final.trim()) {
          const finalTrimmed = final.trim();
          const currentValue = value || '';
          const currentValueTrimmed = currentValue.trim();
          
          if (isMobile) {
            // MOBILE: scrivi blocco per blocco direttamente nell'input
            if (currentValueTrimmed && currentValueTrimmed.includes(finalTrimmed)) {
              console.log('📱 [FreeTextInput] Mobile - Final già presente nell\'input, ignoro:', finalTrimmed);
            } else {
              let newValue;
              
              if (!currentValueTrimmed) {
                newValue = finalTrimmed;
              } else if (finalTrimmed.startsWith(currentValueTrimmed)) {
                newValue = finalTrimmed; // aggiorna blocco preesistente
              } else if (currentValueTrimmed.includes(finalTrimmed)) {
                console.log('📱 [FreeTextInput] Mobile - Final è sottostringa del testo corrente, ignoro:', finalTrimmed);
                newValue = currentValueTrimmed;
              } else {
                newValue = (currentValueTrimmed + ' ' + finalTrimmed).trim();
              }
              
              if (newValue && newValue !== currentValueTrimmed) {
                console.log('📱 [FreeTextInput] Mobile - Aggiorno input con:', newValue);
                onChange(newValue);
                setIsVoiceText(true);
              }
              
              // Traccia l'ultimo blocco mobile
              accumulatedTextRef.current = finalTrimmed;
            }
          } else {
            // DESKTOP: accumula mantenendo il testo completo
            let newValue;
            
            if (!currentValueTrimmed) {
              newValue = finalTrimmed;
            } else if (finalTrimmed.startsWith(currentValueTrimmed)) {
              // Il browser ha fornito il testo completo aggiornato
              newValue = finalTrimmed;
            } else if (currentValueTrimmed.includes(finalTrimmed)) {
              console.log('💻 [FreeTextInput] Desktop - Final già presente, ignoro:', finalTrimmed);
              newValue = currentValueTrimmed;
            } else {
              // Probabilmente è solo l'ultima frase: appendi
              newValue = (currentValueTrimmed + ' ' + finalTrimmed).trim();
            }
            
            if (newValue && newValue !== currentValueTrimmed) {
              console.log('💻 [FreeTextInput] Desktop - Aggiorno input con:', newValue);
              onChange(newValue);
              setIsVoiceText(true);
            }
            
            if (newValue) {
              accumulatedTextRef.current = newValue;
            }
          }
        }
        
        // Se abbiamo richiesto lo stop, l'UI è già fermata
        // Non fare nulla qui, aspetta onend per processare il testo finale
      },
      // onError
      (error) => {
        console.error('❌ [FreeTextInput] Errore riconoscimento vocale:', error);
        isRecordingRef.current = false;
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setIsRecording(false);
        setTimeRemaining(MAX_RECORDING_TIME);
        // Solo se non è un errore di silenzio, mostra alert
        if (error !== 'no-speech' && error !== 'aborted') {
          alert(t('freeTextSearch.voiceError'));
        }
      },
      // onEnd
      () => {
        console.log('🎤 [FreeTextInput] onEnd chiamato, stopRecordingIntentionally:', stopRecordingIntentionally.current, 'isRecordingRef:', isRecordingRef.current, 'timerRef:', !!timerRef.current);
        
        // Se è stato fermato manualmente o è scaduto il timer, processa il testo
        if (stopRecordingIntentionally.current || !isRecordingRef.current || !timerRef.current) {
          console.log('🎤 [FreeTextInput] Fermato manualmente/timeout - Speech Recognition finito, processo testo');
          
          // IMPORTANTE: Aggiungi l'ultimo final all'accumulato se non è già presente
          const currentFinal = finalTranscriptRef.current.trim();
          if (currentFinal) {
            const previousText = accumulatedTextRef.current || '';
            if (!previousText || !previousText.includes(currentFinal)) {
              accumulatedTextRef.current = previousText 
                ? (previousText + ' ' + currentFinal).trim()
                : currentFinal;
              console.log('📝 [FreeTextInput] Testo accumulato (onEnd):', accumulatedTextRef.current);
            }
          }
          
          // Processa il testo ora che Speech Recognition ha finito
          processTextWhenFinished();
          return;
        }
        
        // IMPORTANTE: Su mobile, il testo è già nell'input (aggiunto blocco per blocco)
        // Su desktop, aggiungi l'ultimo final se non è già presente
        const currentFinal = finalTranscriptRef.current.trim();
        console.log('📝 [FreeTextInput] onEnd - currentFinal:', currentFinal);
        
        if (currentFinal && !isMobile) {
          // DESKTOP: Aggiungi l'ultimo final all'accumulato se non è già presente
          const previousText = accumulatedTextRef.current || '';
          if (!previousText || !previousText.includes(currentFinal)) {
            accumulatedTextRef.current = previousText 
              ? (previousText + ' ' + currentFinal).trim()
              : currentFinal;
            console.log('📝 [FreeTextInput] Desktop - Testo accumulato (onEnd):', accumulatedTextRef.current);
          }
        }
        // MOBILE: Non fare nulla, il testo è già nell'input
        
        // Se non è stato fermato manualmente e siamo ancora in registrazione,
        // riavvia Speech Recognition IMMEDIATAMENTE per mantenere il flusso continuo
        if (isRecordingRef.current && timerRef.current) {
          console.log('🔄 [FreeTextInput] Riavvio Speech Recognition...');
          
          // Funzione ricorsiva per riavviare Speech Recognition
          const restartRecognition = () => {
            if (!isRecordingRef.current || stopRecordingIntentionally.current || !timerRef.current) {
              console.log('⚠️ [FreeTextInput] Non riavvio - condizioni non soddisfatte');
              return;
            }
            
            console.log('🔄 [FreeTextInput] Riavvio effettivo Speech Recognition');
            // Reset per la nuova sessione (solo per display, l'accumulato resta)
            setFinalTranscript('');
            setInterimTranscript('');
            finalTranscriptRef.current = '';
            
            // Riavvia con la STESSA logica di accumulo del primo avvio
            speechRecognitionService.start(
              (final, interim) => {
                // USA LA STESSA LOGICA del primo avvio per accumulare
                setFinalTranscript(final);
                finalTranscriptRef.current = final;
                setInterimTranscript(interim);
                interimTranscriptRef.current = interim;
                
                // Gestisci final (stessa logica del primo avvio)
                if (final && final.trim()) {
                  const finalTrimmed = final.trim();
                  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                  
                  if (isMobile) {
                    // MOBILE: Aggiungi direttamente all'input (stessa logica del primo avvio)
                    const currentValue = value || '';
                    const currentValueTrimmed = currentValue.trim();
                    
                    if (currentValueTrimmed.includes(finalTrimmed)) {
                      console.log('📱 [FreeTextInput] Mobile (riavvio) - Final già presente, ignoro:', finalTrimmed);
                      return;
                    }
                    
                    if (finalTrimmed.startsWith(currentValueTrimmed) && currentValueTrimmed.length > 0) {
                      console.log('📱 [FreeTextInput] Mobile (riavvio) - Aggiorno input:', finalTrimmed);
                      onChange(finalTrimmed);
                      setIsVoiceText(true);
                    } else {
                      const newValue = currentValueTrimmed 
                        ? (currentValueTrimmed + ' ' + finalTrimmed).trim()
                        : finalTrimmed;
                      console.log('📱 [FreeTextInput] Mobile (riavvio) - Aggiungo blocco:', finalTrimmed);
                      onChange(newValue);
                      setIsVoiceText(true);
                    }
                    
                    accumulatedTextRef.current = finalTrimmed;
                  } else {
                    // DESKTOP: Aggiorna accumulato e input
                    const previousText = accumulatedTextRef.current || '';
                    if (!previousText || finalTrimmed !== previousText) {
                      accumulatedTextRef.current = finalTrimmed;
                      onChange(finalTrimmed);
                      setIsVoiceText(true);
                    }
                  }
                }
              },
              (error) => {
                if (error !== 'no-speech' && error !== 'aborted') {
                  console.error('Errore riconoscimento vocale:', error);
                }
              },
              () => {
                // onEnd del riavvio - accumula e riavvia se necessario
                console.log('🎤 [FreeTextInput] onEnd riavvio chiamato');
                
                // Aggiungi l'ultimo final all'accumulato
                const currentFinal = finalTranscriptRef.current.trim();
                if (currentFinal) {
                  const previousText = accumulatedTextRef.current || '';
                  if (!previousText || !previousText.includes(currentFinal)) {
                    accumulatedTextRef.current = previousText 
                      ? (previousText + ' ' + currentFinal).trim()
                      : currentFinal;
                    console.log('📝 [FreeTextInput] Testo accumulato (riavvio onEnd):', accumulatedTextRef.current);
                  }
                }
                
                // Se è stato fermato, processa il testo
                if (stopRecordingIntentionally.current || !isRecordingRef.current || !timerRef.current) {
                  console.log('🎤 [FreeTextInput] Fermato durante riavvio - processo testo');
                  processTextWhenFinished();
                  return;
                }
                
                // Altrimenti riavvia di nuovo (ricorsivo)
                setTimeout(() => {
                  restartRecognition();
                }, 50);
              }
            );
          };
          
          // Riavvia immediatamente per flusso continuo
          setTimeout(() => {
            restartRecognition();
          }, 50);
        } else {
          console.log('⚠️ [FreeTextInput] Non riavvio - isRecordingRef:', isRecordingRef.current, 'timerRef:', !!timerRef.current);
        }
      }
    );
    
    if (!started) {
      console.error('❌ [FreeTextInput] Speech Recognition non avviato!');
      isRecordingRef.current = false;
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimeRemaining(MAX_RECORDING_TIME);
    } else {
      console.log('✅ [FreeTextInput] Speech Recognition avviato con successo');
    }
  };

  const handleStopRecording = () => {
    console.log('🛑 [FreeTextInput] handleStopRecording chiamato, isRecording:', isRecording, 'isRecordingRef:', isRecordingRef.current);
    
    // Se non stiamo registrando, non fare nulla
    if (!isRecordingRef.current) {
      console.log('⚠️ [FreeTextInput] Non in registrazione, ignoro stop');
      return;
    }
    
    // STEP 1: Ferma SUBITO l'UI (mic bianco, timer fermo) - l'utente vede che ha premuto stop
    stopUI();
    
    // STEP 2: Ferma Speech Recognition (onend verrà chiamato quando finisce)
    stopSpeechRecognition();
    
    // Il testo verrà processato quando onend viene chiamato (processTextWhenFinished)
  };

  const handleClearText = () => {
    onChange('');
    setFinalTranscript('');
    setInterimTranscript('');
    setIsVoiceText(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = () => {
    if (!value || typeof value !== 'string' || value.trim().length === 0) {
      return;
    }
    
    if (onSubmit) {
      onSubmit(value.trim(), isVoiceText); // Passa il flag se è voce
      // NON pulire automaticamente - l'utente può usare il pulsante X
      setIsVoiceText(false);
      setFinalTranscript('');
      setInterimTranscript('');
      setPendingTranscript('');
      accumulatedTextRef.current = '';
      finalTranscriptRef.current = '';
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange(''); // onChange si aspetta una stringa, non un evento
    }
    setIsVoiceText(false);
    setFinalTranscript('');
    setInterimTranscript('');
    setPendingTranscript('');
    accumulatedTextRef.current = '';
    finalTranscriptRef.current = '';
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.placeholder = '';
    }
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = () => {
    // Delay per permettere click sui pulsanti
    setTimeout(() => {
      const activeElement = document.activeElement;
      
      // Controlla se si sta interagendo con i consigli
      const suggestionsContainer = document.querySelector('.prompt-suggestions-container');
      const isInteracting = suggestionsContainer && 
        suggestionsContainer.getAttribute('data-interacting') === 'true';
      
      // Se si sta interagendo con i consigli (scroll o touch), NON chiamare onBlur
      // Questo previene il cambio di stato durante lo scroll
      if (isInteracting) {
        // Mantieni il focus se siamo in minimal
        const input = inputRef.current;
        if (input && document.activeElement !== input) {
          input.focus();
        }
        return; // Non chiamare onBlur durante l'interazione
      }
      
      // Altrimenti chiama onBlur normalmente
      if (inputRef.current) {
        inputRef.current.placeholder = placeholder || t('freeTextSearch.placeholder');
      }
      if (onBlur) {
        onBlur();
      }
    }, 200);
  };

  return (
    <div className="free-text-input-container" style={{ position: 'relative', width: '100%' }}>
      <style>{`
        .free-text-input-container textarea::-webkit-scrollbar {
          width: 6px;
        }
        .free-text-input-container textarea::-webkit-scrollbar-track {
          background: transparent;
          margin: 4px 0;
        }
        .free-text-input-container textarea::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
          margin: 2px 0;
        }
        .free-text-input-container textarea::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%'
      }}>
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => {
            handleInputChange(e);
            // Auto-resize textarea - espandi fino a maxHeight, poi scroll
            const textarea = e.target;
            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const maxHeight = 80; // maxHeight in pixel
            
            if (scrollHeight > maxHeight) {
              textarea.style.height = maxHeight + 'px';
              textarea.style.overflowY = 'auto';
            } else {
              textarea.style.height = scrollHeight + 'px';
              textarea.style.overflowY = 'hidden';
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={className}
          placeholder={placeholder || t('freeTextSearch.placeholder')}
          style={{
            ...style,
            paddingRight: '50px',
            resize: 'none',
            overflowY: 'auto',
            minHeight: className?.includes('search-hero-input') ? '44px' : '50px',
            maxHeight: className?.includes('search-hero-input') ? '96px' : '80px',
            lineHeight: '1.5',
            textAlign: 'left',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.3) transparent'
          }}
          disabled={disabled || isRecording}
          rows={className?.includes('search-hero-input') ? 2 : 1}
        />
        
          {/* Pulsante X per pulire - in alto a destra */}
          {value && typeof value === 'string' && value.trim().length > 0 && !isRecording && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                position: 'absolute',
                right: '10px',
                top: '0',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                transition: 'color 0.2s',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }}
              title={t('freeTextSearch.clear') || 'Pulisci'}
            >
              <i className="bi bi-x" style={{ fontSize: '22px' }} />
            </button>
          )}

          {/* Icona microfono o invio - spostata più in basso */}
          <div style={{
            position: 'absolute',
            right: '10px',
            bottom: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Pulsante microfono o invio */}
            {showMicrophone && !isRecording && (
              <button
                type="button"
                onClick={handleStartRecording}
                disabled={disabled}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  opacity: disabled ? 0.5 : 1,
                  transition: 'opacity 0.2s'
                }}
                title={t('freeTextSearch.recording')}
              >
                <i className="bi bi-mic" style={{ fontSize: '18px' }} />
              </button>
            )}

            {/* Quando registra, mostra SEMPRE mic rosso che pulsa */}
            {isRecording && (
              <button
                type="button"
                onClick={handleStopRecording}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ff4444',
                  animation: 'pulse 1s infinite'
                }}
                title={t('freeTextSearch.stopRecording')}
              >
                <i className="bi bi-mic-fill" style={{ fontSize: '18px', color: '#ff4444' }} />
              </button>
            )}

            {!showMicrophone && !isRecording && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={disabled || !value || typeof value !== 'string' || value.trim().length === 0}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: disabled || !value || typeof value !== 'string' || value.trim().length === 0 ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  opacity: disabled || !value || typeof value !== 'string' || value.trim().length === 0 ? 0.5 : 1,
                  transition: 'opacity 0.2s'
                }}
                title={t('freeTextSearch.send')}
              >
                <i className="bi bi-send" style={{ fontSize: '18px' }} />
              </button>
            )}
          </div>
      </div>

      {/* Contatore parole — nascosto sulla search desktop (search-hero-input); resta sul mobile */}
      {!className?.includes('search-hero-input') && (
      <div style={{
        marginTop: '6px',
        fontSize: '12px',
        color: wordCount >= MAX_WORDS ? '#ff4444' : (wordCount > 0 ? '#888' : '#666'),
        textAlign: 'right',
        paddingRight: '4px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>
          {wordCount}/{MAX_WORDS} {t('freeTextSearch.wordsLabel')}
        </span>
        {isRecording && (
          <span 
            key={`timer-${timeRemaining}`}
            style={{ 
              color: '#ff4444', 
              fontSize: '11px', 
              fontWeight: 'bold',
              minWidth: '30px',
              display: 'inline-block',
              textAlign: 'center'
            }}
          >
            {timeRemaining}s
          </span>
        )}
      </div>
      )}


      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default FreeTextInput;

